class MouseParallax {
  constructor(element) {
    this.element = element;
    if (!this.element) return;

    // Get configuration from data attributes with defaults
    this.config = {
      strength: parseInt(this.element.dataset.strength) || 50,
      ease: parseFloat(this.element.dataset.ease) || 0.1,
    };

    // Get initial bounds
    this.updateBounds();

    // State
    this.state = {
      targetX: 0,
      targetY: 0,
      currentX: 0,
      currentY: 0,
    };

    // Bind methods
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.update = this.update.bind(this);
    this.onResize = this.onResize.bind(this);

    // Initialize
    this.init();
  }

  init() {
    // Only add listeners if device supports hover
    if (window.matchMedia("(hover: hover)").matches) {
      this.element.addEventListener("mousemove", this.onMouseMove, { passive: true });
      this.element.addEventListener("mouseleave", this.onMouseLeave, { passive: true });
      window.addEventListener("resize", this.onResize, { passive: true });

      // Start animation loop
      this.animationFrame = requestAnimationFrame(this.update);
    }
  }

  onMouseMove(event) {
    const { strength } = this.config;
    const bounds = this.element.getBoundingClientRect();

    // Get relative mouse position using page coordinates
    const relativeX = event.pageX - (bounds.left + window.pageXOffset) - bounds.width / 2;
    const relativeY = event.pageY - (bounds.top + window.pageYOffset) - bounds.height / 2;

    // Calculate target position with boundaries
    this.state.targetX = Math.max(Math.min(relativeX / strength, 30), -30);
    this.state.targetY = Math.max(Math.min(relativeY / strength, 30), -30);
  }

  onMouseLeave() {
    // Reset to initial position when mouse leaves
    this.state.targetX = 0;
    this.state.targetY = 0;
  }

  update() {
    const { currentX, currentY, targetX, targetY } = this.state;
    const { ease } = this.config;

    // Smoothly interpolate current position to target
    this.state.currentX += (targetX - currentX) * ease;
    this.state.currentY += (targetY - currentY) * ease;

    // Apply transform with rounded values for better performance
    const x = Math.round(this.state.currentX * 100) / 100;
    const y = Math.round(this.state.currentY * 100) / 100;

    this.element.style.transform = `translate3d(${x}px, ${y}px, 0)`;

    // Continue animation loop
    this.animationFrame = requestAnimationFrame(this.update);
  }

  updateBounds() {
    // Get current bounds
    this.bounds = this.element.getBoundingClientRect();
  }

  onResize() {
    // Update bounds on resize
    this.updateBounds();
  }

  destroy() {
    if (this.element) {
      this.element.removeEventListener("mousemove", this.onMouseMove);
      this.element.removeEventListener("mouseleave", this.onMouseLeave);
      window.removeEventListener("resize", this.onResize);
      cancelAnimationFrame(this.animationFrame);
      this.element.style.transform = "";
    }
  }
}

// Initialize all mouse parallax elements
class MouseParallaxManager {
  constructor() {
    this.instances = new Map();
    this.init();
  }

  init() {
    // Initialize existing elements
    this.initElements();

    // Watch for new elements (if using dynamic content)
    this.observe();
  }

  initElements() {
    const elements = document.querySelectorAll(".mouse-parallax");
    elements.forEach((element) => {
      if (!this.instances.has(element)) {
        const instance = new MouseParallax(element);
        this.instances.set(element, instance);
      }
    });
  }

  observe() {
    // Optional: Use MutationObserver to watch for dynamically added elements
    if (typeof MutationObserver !== "undefined") {
      this.observer = new MutationObserver((mutations) => {
        let shouldInit = false;
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList?.contains("mouse-parallax") || node.querySelector?.(".mouse-parallax")) {
                shouldInit = true;
              }
            }
          });
        });

        if (shouldInit) {
          this.initElements();
        }
      });

      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  destroy() {
    // Clean up all instances
    this.instances.forEach((instance) => instance.destroy());
    this.instances.clear();

    // Disconnect observer
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Initialize on DOM load
document.addEventListener("DOMContentLoaded", () => {
  window.mouseParallaxManager = new MouseParallaxManager();

  // Cleanup on page unload
  window.addEventListener("unload", () => {
    if (window.mouseParallaxManager) {
      window.mouseParallaxManager.destroy();
    }
  });
});
